import * as yup from 'yup';
import {
  Button,
  Container,
  FileUpload,
  Form,
  Group,
  IconButton,
  Link,
  StatusIconOutlined,
  Text,
  Tooltip,
  useBreakpoint,
  useMelioForm,
  useTheme,
} from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';

import { InfoCard } from '../../../cl/components/InfoCard/InfoCard.component';
import { W9SubmissionMethod } from './types';

type Fields = {
  W9SubmissionMethod: W9SubmissionMethod;
};

const schema = yup.object().shape({
  W9SubmissionMethod: yup.string().required(),
}) as yup.SchemaOf<Fields>;

export const TaxPayerInfoForm = ({ handleSendClick }: { handleSendClick?: () => void }) => {
  const { isExtraSmallScreen } = useBreakpoint();
  const theme = useTheme();
  const { formatMessage } = useMelioIntl();
  const {
    settings: { tax1099LearnMoreLink },
  } = useConfig();

  const { registerField, formProps, watch } = useMelioForm({
    schema,
    onSubmit: () => null,
    defaultValues: { W9SubmissionMethod: W9SubmissionMethod.Email },
    subscribeToDefaultValuesChanges: true,
  });
  const selectedW9SubmissionMethod = watch('W9SubmissionMethod');

  const options = [
    {
      mainLabelProps: {
        label: formatMessage('widgets.taxPayerInfo.form.radioGroup.email'),
      },
      value: W9SubmissionMethod.Email,
    },
    {
      mainLabelProps: {
        label: formatMessage('widgets.taxPayerInfo.form.radioGroup.pdf'),
      },
      value: W9SubmissionMethod.Pdf,
    },
  ];

  return (
    <Container>
      <Group
        data-testid="taxpayer-form"
        spacing="m"
        hasDivider={!isExtraSmallScreen}
        variant={isExtraSmallScreen ? 'vertical' : 'horizontal'}
      >
        <Group width="full" flexDirection="column" spacing={'s'}>
          <Container gap={'xs'} alignItems={'center'} display={'flex'}>
            <Text textStyle="body2Semi">{formatMessage('widgets.taxPayerInfo.form.title')}</Text>
            <Tooltip label={formatMessage('widgets.taxPayerInfo.form.title.tooltip')}>
              <StatusIconOutlined variant="neutral" color={theme.colors.neutral.darker} size="small" />
            </Tooltip>
          </Container>
          <Form gap={'m'} data-testid="taxpayer-info-form" {...formProps}>
            <Form.RadioGroup
              {...registerField('W9SubmissionMethod')}
              options={options}
              variant="horizontal"
              aria-label="Taxpayer submission form type"
            />
            {selectedW9SubmissionMethod === W9SubmissionMethod.Pdf && (
              <Group
                data-testid="taxpayer-form-file-upload"
                width="full"
                variant="horizontal"
                justifyContent="space-between"
                spacing="s"
              >
                <FileUpload width="100%" placeholder={formatMessage('widgets.taxPayerInfo.form.upload.placeholder')} />
              </Group>
            )}
            {selectedW9SubmissionMethod === W9SubmissionMethod.Email && (
              <Group
                variant={isExtraSmallScreen ? 'vertical' : 'horizontal'}
                justifyContent="space-between"
                spacing="s"
              >
                <InfoCard
                  icon={<IconButton variant="naked" size="small" icon="help-circle" />}
                  label={formatMessage('widgets.taxPayerInfo.info.label', {
                    learnMoreLink: (
                      <Text textStyle="body4" color={theme.colors.brand.main}>
                        <Link
                          variant="inline"
                          color={theme.colors.brand.main}
                          href={tax1099LearnMoreLink}
                          label={formatMessage('widgets.taxPayerInfo.info.link')}
                          newTab
                        />
                      </Text>
                    ),
                  })}
                />
                <Button
                  variant="tertiary"
                  icon="send"
                  label={formatMessage('widgets.taxPayerInfo.form.submit.request')}
                  data-testid={'taxpayer-request-btn'}
                  onClick={handleSendClick}
                />
              </Group>
            )}
          </Form>
        </Group>
      </Group>
    </Container>
  );
};

TaxPayerInfoForm.displayName = 'TaxPayerInfoForm';
